import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { paycorApi, usersApi, type PaycorSyncRun } from '@/lib/apiClient';
import { Button } from '@/components';

/**
 * Paycor hours + roster feed status and manual trigger (Admin › Integrations).
 *
 * ⚠️ FRESHNESS IS "DID THE JOB RUN", NOT "HOW RECENT IS THE NEWEST work_date".
 * The old feed went stale for nine days and nothing surfaced it. Reading max
 * work_date would have been no help either: it is confounded by weekends,
 * holidays and shutdown weeks, so a quiet Sunday looks identical to a broken
 * feed. This panel leads with hours since the last SUCCESSFUL run (issue #388).
 *
 * Three templates are pulled every day and they cover different failures —
 * losing the daily costs yesterday's hours, losing the weekly costs every
 * retroactive correction, losing the roster means new hires never appear and
 * leavers are still chased. Each is reported on its own line rather than rolled
 * into one status, so a healthy leg cannot mask a dead one.
 */

const LABEL: Record<string, string> = {
  daily: 'Daily (yesterday)',
  weekly: 'Weekly (previous work week)',
  roster: 'Employee roster',
  manual: 'Manual',
  backfill: 'Backfill',
};

const fmtWhen = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : '—');

/**
 * What one run actually changed.
 *
 * Row counts and total hours can BOTH be unmoved by a real correction — swapping
 * a cost code, or moving hours from one employee to another, changes neither. So
 * a run with revisions but no counter movement is reported as a content
 * correction rather than as "no change".
 */
function renderChange(r: PaycorSyncRun): string {
  const added = r.rows_added ?? 0;
  const removed = r.rows_removed ?? 0;
  const delta = Number(r.hours_delta ?? 0);
  const revisions = r.revisions ?? 0;
  // ⚠️ A DATED run that refreshed no dates is not a normal quiet run: the report
  // came back with nothing, which is a shutdown week or a broken template, and
  // nothing was cleared either way because an empty report says nothing about
  // where its window sits. Calling it out is the only honest option.
  //
  // Dated reports ONLY. The roster is a full employee snapshot with no work dates
  // at all, so its dates_covered is legitimately 0 and this test would brand
  // every healthy roster pull an empty report.
  if (r.source !== 'roster' && r.status === 'success' && (r.dates_covered ?? 0) === 0) {
    return 'report returned no rows — nothing refreshed';
  }
  // ⚠️ Real churn, not a headcount delta. Equal hires and leavers would read
  // "no change" from totals alone, and a promotion to Foreman — which decides who
  // owes a vehicle inspection — would not show at all.
  if (r.source === 'roster') {
    const changed = r.rows_changed ?? 0;
    const parts = [];
    if (added) parts.push(`+${added} joined`);
    if (removed) parts.push(`−${removed} left`);
    if (changed) parts.push(`${changed} updated`);
    return `${r.rows_stored ?? 0} employees${parts.length ? ' · ' + parts.join(' · ') : ' · no change'}`;
  }
  if (added || removed || Math.abs(delta) >= 0.01) {
    return `+${added} / −${removed} (${fmtNum(r.hours_delta)} h)`;
  }
  if (revisions) {
    return `${revisions} date${revisions === 1 ? '' : 's'} corrected — same rows and hours`;
  }
  return 'no change';
}
const fmtNum = (n: number | null | undefined) =>
  n === null || n === undefined ? '—' : Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 });

export function PaycorSyncPanel() {
  const qc = useQueryClient();

  const { data: s } = useQuery({
    queryKey: ['paycor-status'],
    queryFn: () => paycorApi.status(),
    staleTime: 10_000,
    refetchInterval: (q) => (q.state.data?.running ? 4_000 : false),
  });

  const { data: me } = useQuery({
    queryKey: ['me'],
    queryFn: () => usersApi.me(),
    staleTime: 5 * 60 * 1000,
  });
  const canSync = (me?.roles.includes('Admin') ?? false) || (me?.permissions.includes('admin.access') ?? false);

  const syncMut = useMutation({
    mutationFn: () => paycorApi.sync(),
    onSuccess: () => {
      // The trigger returns { started } and runs in the background — refetch the
      // real status rather than caching the trigger response.
      qc.invalidateQueries({ queryKey: ['paycor-status'] });
      qc.invalidateQueries({ queryKey: ['manpower'] });
    },
  });

  if (!s) return <p style={{ color: 'var(--ew-n-500)', fontSize: 'var(--ew-fs-body-sm)' }}>Loading…</p>;

  if (!s.configured) {
    // ⚠️ A missing TEMPLATE id is the one gap the lookup exists to fill, so it
    // has to render here too — otherwise the only way to find the id is hidden
    // by the very setting that needs it. Missing credentials still hide it:
    // the lookup cannot reach Paycor without them. (Codex, #551.)
    const onlyTemplates =
      (s.missingConfig?.length ?? 0) > 0 && s.missingConfig!.every((n) => n.startsWith('PAYCOR_TEMPLATE_'));
    return (
      <div>
        <p style={{ color: 'var(--ew-n-500)', fontSize: 'var(--ew-fs-body-sm)', lineHeight: 1.5 }}>
          Paycor is not fully configured on this environment, so the feed is idle.
          {s.missingConfig?.length ? ` Missing: ${s.missingConfig.join(', ')}.` : ''}
        </p>
        {canSync && onlyTemplates && <TemplateLookup />}
      </div>
    );
  }

  // 36 h rather than 24: a nightly job that runs at 04:00 is legitimately ~24 h
  // old just before the next run, and alerting on that would cry wolf daily.
  //
  // ⚠️ hoursSinceLastSuccess is the age of the STALEST report leg,
  // not the most recent run of any kind. A daily pull that keeps succeeding must
  // never make the banner green while the weekly leg — the one that repairs
  // retroactive edits — has been failing every night.
  // ⚠️ A day nothing covered keeps the feed unhealthy even when every leg ran
  // recently. The daily window moves on, so a failed night leaves a hole that
  // the next night's success would otherwise paper over — and the weekly report
  // may not reach it until its work week rolls over, up to a week later.
  const gaps = s.missingDates ?? [];
  const hrs = s.hoursSinceLastSuccess;
  const stale = hrs === null || hrs > 36 || gaps.length > 0;
  const legNote = [
    s.dailyAgeHours === null ? 'daily: never' : `daily: ${Math.floor(s.dailyAgeHours)} h ago`,
    s.weeklyAgeHours === null ? 'weekly: never' : `weekly: ${Math.floor(s.weeklyAgeHours)} h ago`,
    s.rosterAgeHours === null ? 'roster: never' : `roster: ${Math.floor(s.rosterAgeHours)} h ago`,
  ].join(' · ');

  return (
    <div>
      {/* ⚠️ Say plainly when nothing is scheduled. A green freshness banner sitting
          under copy that promises a nightly run is how a dead feed looks alive —
          and it shipped with the schedule OFF until the cutover was reconciled. */}
      {!s.schedule?.enabled && (
        <div
          style={{
            padding: '10px 12px',
            marginBottom: 12,
            borderRadius: 'var(--ew-radius-sm)',
            background: 'var(--ew-warning-50, #fff8e6)',
            border: '1px solid var(--ew-warning, #b8860b)',
            fontSize: 'var(--ew-fs-body-sm)',
            lineHeight: 1.5,
          }}
        >
          <strong>The nightly sync is turned off.</strong> Hours and the roster only update when someone
          presses Sync now — new hires will not appear and leavers will still be counted. Switch{' '}
          <code>PAYCOR_SYNC_ENABLED</code> to <code>true</code> to start the {s.schedule?.cron ?? '0 4 * * *'}{' '}
          schedule.
        </div>
      )}
      {/* ⚠️ A roster can sync successfully every night and still be wrong. The
          original roster report returned the same list for a month — leavers
          still counted, hires missing — while every run said "no change"
          (#550). Amber, not red: the feed is running; the report is suspect. */}
      {s.rosterUnchangedWarn && (
        <div
          style={{
            padding: '10px 12px',
            marginBottom: 12,
            borderRadius: 'var(--ew-radius-sm)',
            background: 'var(--ew-warning-50, #fff8e6)',
            border: '1px solid var(--ew-warning, #b8860b)',
            fontSize: 'var(--ew-fs-body-sm)',
            lineHeight: 1.5,
          }}
        >
          <strong>The employee roster has not changed in {s.rosterUnchangedDays} days.</strong> Every sync
          still succeeds, so check the report itself: open the roster report in Paycor and confirm it returns
          current employees — recent hires present, leavers gone. A previous roster report returned the same
          list for a month this way.
        </div>
      )}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '10px 12px',
          borderRadius: 'var(--ew-radius-sm)',
          background: stale ? 'var(--ew-danger-50, #fdeaea)' : 'var(--ew-n-50, #f6f7f8)',
          border: `1px solid ${stale ? 'var(--ew-danger, #c0392b)' : 'var(--ew-n-200, #e3e6e8)'}`,
          marginBottom: 14,
        }}
      >
        <div style={{ flex: 1, fontSize: 'var(--ew-fs-body-sm)', lineHeight: 1.5 }}>
          <strong style={{ color: stale ? 'var(--ew-danger, #c0392b)' : 'var(--ew-dark)' }}>
            {s.running
              ? 'Sync running…'
              : hrs === null
                ? 'One or more reports have never completed successfully'
                : gaps.length
                  ? `${gaps.length} day${gaps.length === 1 ? '' : 's'} not covered by any successful run`
                  : hrs > 36
                    ? `Stalest report last succeeded ${Math.floor(hrs)} h ago`
                    : // Count-agnostic on purpose: "Both" was already wrong once the
                      // roster became a third leg, and a hardcoded "three" would go
                      // the same way. The per-leg line below names them anyway.
                      `All reports current — stalest ${Math.floor(hrs)} h ago`}
          </strong>
          {gaps.length > 0 && (
            <div style={{ color: 'var(--ew-danger, #c0392b)' }}>
              Missing: {gaps.slice(0, 8).join(', ')}
              {gaps.length > 8 ? ` and ${gaps.length - 8} more` : ''}. A later run covering those days clears this.
            </div>
          )}
          <div style={{ color: 'var(--ew-n-500)' }}>{legNote}</div>
          <div style={{ color: 'var(--ew-n-500)' }}>
            {fmtNum(s.feed.rows_total)} rows{' '}
            {s.feed.min_date && s.feed.max_date ? `· ${s.feed.min_date} → ${s.feed.max_date}` : ''}{' '}
            · {fmtNum(s.feed.field_hours)} field hours
          </div>
        </div>
        {canSync && (
          <Button onClick={() => syncMut.mutate()} disabled={s.running || syncMut.isPending}>
            {s.running || syncMut.isPending ? 'Syncing…' : 'Sync now'}
          </Button>
        )}
      </div>

      {syncMut.isError && (
        <p style={{ color: 'var(--ew-danger, #c0392b)', fontSize: 'var(--ew-fs-body-sm)', marginBottom: 12 }}>
          {(syncMut.error as Error).message}
        </p>
      )}

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--ew-fs-body-sm)' }}>
        <thead>
          <tr style={{ textAlign: 'left', color: 'var(--ew-n-500)' }}>
            <th style={TH}>Report</th>
            <th style={TH}>Last run</th>
            <th style={TH}>Covered</th>
            <th style={TH}>Rows</th>
            <th style={TH}>Change</th>
          </tr>
        </thead>
        <tbody>
          {s.runs.length === 0 && (
            <tr>
              <td colSpan={5} style={{ ...TD, color: 'var(--ew-n-500)' }}>
                No runs yet.
              </td>
            </tr>
          )}
          {s.runs.map((r: PaycorSyncRun) => (
            <tr key={r.id}>
              <td style={TD}>{LABEL[r.source ?? ''] ?? r.source ?? '—'}</td>
              <td style={TD}>
                <span style={{ color: r.status === 'error' ? 'var(--ew-danger, #c0392b)' : 'inherit' }}>
                  {fmtWhen(r.finished_at ?? r.started_at)}
                  {r.status === 'error' ? ' · failed' : ''}
                  {r.run_by_name ? ` · ${r.run_by_name}` : ' · scheduled'}
                </span>
                {r.error && (
                  <div style={{ color: 'var(--ew-danger, #c0392b)', whiteSpace: 'pre-wrap', maxWidth: 360 }}>
                    {r.error}
                  </div>
                )}
              </td>
              <td style={TD}>
                {r.date_from && r.date_to
                  ? r.date_from === r.date_to
                    ? r.date_from
                    : `${r.date_from} → ${r.date_to}`
                  : '—'}
              </td>
              <td style={TD}>{fmtNum(r.rows_stored)}</td>
              <td style={TD}>
                {/* ⚠️ A correction can move NONE of these counters. One entry
                    changed from 8 h to 4 h leaves the row count alone; a cost
                    code corrected, or hours moved between two people, leaves the
                    total alone too. Both still record a revision, so `revisions`
                    is the last word — otherwise this cell reads "no change"
                    directly above evidence saying a date was corrected. */}
                {renderChange(r)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* The evidence that decides whether the correction window is wide enough. */}
      <p style={{ marginTop: 14, color: 'var(--ew-n-500)', fontSize: 'var(--ew-fs-body-sm)', lineHeight: 1.5 }}>
        {s.lateEdits?.n
          ? `${fmtNum(s.lateEdits.n)} retroactive edits seen — worst arrived ${s.lateEdits.max_lag_days} days
             after the work date (average ${fmtNum(s.lateEdits.avg_lag_days)}). The weekly re-pull corrects a
             date for about 13 days; if the worst case approaches that, the correction window is too narrow.`
          : 'No retroactive edits recorded yet. Paycor hours are edited after the fact, so this counter is how we learn how wide the correction window needs to be.'}
      </p>

      {canSync && <TemplateLookup />}
    </div>
  );
}

/**
 * Paycor's report templates with their ids (#550).
 *
 * A PAYCOR_TEMPLATE_* setting needs an id that Paycor's own screens do not
 * show, so switching a feed to a new report used to stall on finding it. This
 * asks Paycor and lists every template the report user can download.
 *
 * ⚠️ ON DEMAND, NEVER ON LOAD. It calls Paycor directly, Paycor rate-limits,
 * and nobody needs it except while changing a setting.
 */
function TemplateLookup() {
  const [open, setOpen] = useState(false);
  const lookup = useMutation({ mutationFn: () => paycorApi.reportTemplates() });
  const d = lookup.data;
  const small = { fontSize: 'var(--ew-fs-body-sm)', lineHeight: 1.5 } as const;

  return (
    <div style={{ marginTop: 18 }}>
      <Button
        onClick={() => {
          setOpen(true);
          lookup.mutate();
        }}
        disabled={lookup.isPending}
      >
        {lookup.isPending ? 'Asking Paycor…' : 'Look up template ids'}
      </Button>
      {open && lookup.isError && (
        <p style={{ ...small, color: 'var(--ew-danger, #c0392b)', marginTop: 8 }}>
          {(lookup.error as Error).message}
        </p>
      )}
      {open && d && (
        <div style={{ marginTop: 10 }}>
          <p style={{ ...small, color: 'var(--ew-n-500)', marginBottom: 8 }}>
            Copy an id into the matching <code>PAYCOR_TEMPLATE_*</code> app setting — in the portal and in{' '}
            <code>infra/main.bicep</code>, or the next infrastructure deploy removes it.
          </p>
          {d.truncated && (
            <p style={{ ...small, color: 'var(--ew-danger, #c0392b)' }}>
              Paycor has more templates than this lists — the lookup stops after ten pages. The one you need may be past the cutoff.
            </p>
          )}
          {d.configuredNotListed.length > 0 && (
            <p style={{ ...small, color: 'var(--ew-danger, #c0392b)' }}>
              {d.configuredNotListed.join(', ')} {d.configuredNotListed.length === 1 ? 'holds an id' : 'hold ids'}{' '}
              Paycor did not list, so the next sync of {d.configuredNotListed.length === 1 ? 'that report' : 'those reports'}{' '}
              will fail.
            </p>
          )}
          <table style={{ width: '100%', borderCollapse: 'collapse', ...small }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--ew-n-500)' }}>
                <th style={TH}>Template</th>
                <th style={TH}>Id</th>
                <th style={TH}>Used by</th>
              </tr>
            </thead>
            <tbody>
              {d.templates.length === 0 && (
                <tr>
                  <td colSpan={3} style={{ ...TD, color: 'var(--ew-n-500)' }}>
                    Paycor answered but listed no templates for the report user. The daily, weekly and roster
                    reports are unaffected — this lookup is a different call from the one the sync uses.
                    {/* Keys and counts only, so an empty answer says WHY it is empty:
                        a renamed field, an empty list, or not JSON at all. */}
                    {d.shape?.length ? (
                      <div style={{ marginTop: 6 }}>
                        What Paycor sent: <code style={{ wordBreak: 'break-all' }}>{d.shape.join(' · ')}</code>
                      </div>
                    ) : null}
                  </td>
                </tr>
              )}
              {d.templates.map((t) => (
                <tr key={t.templateId}>
                  <td style={TD}>{t.displayName ?? '—'}</td>
                  <td style={TD}>
                    <code style={{ userSelect: 'all' }}>{t.templateId}</code>
                  </td>
                  <td style={TD}>{t.inUse.length ? t.inUse.join(', ') : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const TH: React.CSSProperties = {
  padding: '6px 8px',
  borderBottom: '1px solid var(--ew-n-200, #e3e6e8)',
  fontWeight: 'var(--ew-fw-semibold)' as never,
};
const TD: React.CSSProperties = {
  padding: '6px 8px',
  borderBottom: '1px solid var(--ew-n-100, #f0f2f3)',
  verticalAlign: 'top',
};
